import type { ConcertUploadCustomerDocumentRequestDto } from "../dto/concert.dto"

/**
 * One Concert stipulation upload, for every proposal kind: the same file rules
 * the gateway enforces (PDF, PNG or JPEG of at most 2 MiB) and the base64 body
 * it expects. Identity comes from the stipulation; the server resolves the
 * Concert requirement itself.
 */

export const CONCERT_DOCUMENT_MAX_BYTES = 2 * 1024 * 1024
export const CONCERT_DOCUMENT_ACCEPT = `.pdf,.png,.jpg,.jpeg`
export const CONCERT_DOCUMENT_TYPE_MESSAGE = `Supporting documents must be a PDF, PNG, or JPEG of at most 2 MB.`

const ACCEPTED_EXTENSIONS = new Set([`pdf`, `png`, `jpg`, `jpeg`])

/** The file was refused before any request; nothing on the server moved. */
export class ConcertDocumentValidationError extends Error {}

const fileExtension = (filename: string): string | null => {
  const start = filename.lastIndexOf(`.`)
  if (start < 0 || start === filename.length - 1) {
    return null
  }
  return filename.slice(start + 1).toLowerCase()
}

const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => {
      reject(new Error(`Failed to read the selected file.`))
    }
    reader.onload = () => {
      const base64 =
        typeof reader.result === `string`
          ? reader.result.replace(/^data:.*?;base64,/, ``)
          : ``
      if (base64.length === 0) {
        reject(new Error(`Failed to read the selected file.`))
        return
      }
      resolve(base64)
    }
    reader.readAsDataURL(file)
  })

/** The upload body for a file, or a validation error the seller can fix. */
export const concertDocumentUploadBody = async (
  file: File,
  requirementId: string,
): Promise<ConcertUploadCustomerDocumentRequestDto> => {
  const extension = fileExtension(file.name)
  if (
    extension == null ||
    !ACCEPTED_EXTENSIONS.has(extension) ||
    file.size === 0 ||
    file.size > CONCERT_DOCUMENT_MAX_BYTES
  ) {
    throw new ConcertDocumentValidationError(CONCERT_DOCUMENT_TYPE_MESSAGE)
  }

  return {
    file_contents: await readAsBase64(file),
    filename: file.name,
    filetype: extension,
    filesize: file.size,
    reqId: requirementId,
    type: `stip`,
  }
}
